/**
 * CxL3 — motor control centres.
 *
 * An MCC is a switchboard with motors behind it, so most of its script is the LV
 * switchboard catalogue: `VIS-BOARD`, `VT-3PH`, `PR`, `ELI`, the breaker
 * operation tests and the meter verification all name `MCC` in their
 * `equipmentTypes` and appear automatically.
 *
 * What is here is only what a switchboard does not have: the starter, the
 * overload, the direction the motor actually turns, and the control that stops
 * it. They are scoped **per starter** rather than per board, because a 40-way MCC
 * with one overload set wrong is a 40-way MCC with one burnt motor.
 *
 * Draft defaults. No CxA MCC script has been issued yet.
 */

import type { CatalogueTest } from '../catalogue.js';

export const CXL3_MCC: readonly CatalogueTest[] = [
  {
    code: 'MCC-STARTER',
    name: 'Starter functional test',
    cxLevel: 'CxL3',
    equipmentTypes: ['MCC'],
    breakerTypes: ['Starter'],
    scopes: ['breaker'],
    defaultSelected: true,
    section: 'Functional Test',
    note: 'Per starter. Record the starter type — DOL, star-delta, soft start or VSD — against each way.',
    checks: [
      { label: 'Starter type matches the approved schedule', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Start command from local control', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Contactor pulls in; motor runs; RUN indication at the starter' },
      { label: 'Stop command from local control', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Motor stops; indication clears' },
      { label: 'Start and stop from remote / BMS', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Same result from the remote point' },
      { label: 'Star-delta or soft-start transition clean, no stall or re-trip', responseType: 'yes_no_applicable', toleranceMode: 'none' },
      { label: 'Local / remote selector operates and is left as designed', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Running current on each phase', responseType: 'measured_pass_fail', unit: 'A', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: ['MCC-STARTER-PLATE', 'MCC-STARTER-RUN', 'MCC-STARTER-CURRENT'],
  },
  {
    code: 'MCC-OL',
    name: 'Overload setting and trip',
    cxLevel: 'CxL3',
    equipmentTypes: ['MCC'],
    breakerTypes: ['Starter'],
    scopes: ['breaker'],
    defaultSelected: true,
    section: 'Functional Test',
    note: 'The setting is photographed as well as recorded. A number typed into a report is not evidence that the dial was moved.',
    checks: [
      { label: 'Motor full-load current from the nameplate', responseType: 'measured_pass_fail', unit: 'A', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Overload set to the approved value', responseType: 'measured_pass_fail', unit: 'A', tolerance: 5, toleranceMode: 'percent', requiresPhoto: true },
      { label: 'Overload trip proved (test function or injection)', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Starter trips; TRIP indication locally and at BMS' },
      { label: 'Trip reset restores the starter to ready', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Single-phase / phase-loss protection where fitted', responseType: 'yes_no_applicable', toleranceMode: 'none' },
    ],
    shotCodes: ['MCC-OL-NAMEPLATE', 'MCC-OL-SETTING', 'MCC-OL-TRIP'],
  },
  {
    code: 'MCC-ROT',
    name: 'Motor rotation direction',
    cxLevel: 'CxL3',
    equipmentTypes: ['MCC'],
    breakerTypes: ['Starter'],
    scopes: ['breaker'],
    defaultSelected: true,
    section: 'Phase Rotation',
    note: 'Bumped and observed at the machine, with the mechanical trade present. A pump run backwards is found here or not at all.',
    checks: [
      { label: 'Supply phase rotation at the starter', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Motor turns in the direction marked on the machine', responseType: 'pass_fail_na', toleranceMode: 'none', acceptanceCriteria: 'Matches the arrow on the driven equipment', requiresPhoto: true },
      { label: 'Direction confirmed with the mechanical trade and recorded', responseType: 'pass_fail_na', toleranceMode: 'none' },
    ],
    shotCodes: ['MCC-ROT-METER', 'MCC-ROT-ARROW'],
  },
  {
    code: 'MCC-ESTOP',
    name: 'Emergency stop and safety interlocks',
    cxLevel: 'CxL3',
    equipmentTypes: ['MCC'],
    breakerTypes: ['Starter'],
    scopes: ['breaker'],
    defaultSelected: true,
    section: 'Interlocking Checks',
    checks: [
      { label: 'Field emergency stop drops the starter', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Motor stops immediately; restart blocked until reset', requiresPhoto: true },
      { label: 'Local isolator padlockable and proved to break the circuit', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Process interlock (flow, level, pressure, damper) proved', responseType: 'yes_no_applicable', toleranceMode: 'none' },
      { label: 'Fire alarm shutdown signal where required', responseType: 'yes_no_applicable', toleranceMode: 'none' },
    ],
    shotCodes: ['MCC-ESTOP', 'MCC-ISOLATOR'],
  },
  {
    code: 'MCC-VSD',
    name: 'VSD parameters and operation',
    cxLevel: 'CxL3',
    equipmentTypes: ['MCC'],
    breakerTypes: ['Starter'],
    scopes: ['breaker'],
    defaultSelected: false,
    section: 'Functional Test',
    note: 'Off by default: only ticked for the ways that actually carry a drive.',
    checks: [
      { label: 'Drive parameters match the approved schedule', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Motor nameplate data entered into the drive', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Speed follows the control signal across its range', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Speed tracks the 0–100% demand with no fault' },
      { label: 'Minimum and maximum speed limits set as designed', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Ramp up and ramp down times as scheduled', responseType: 'measured_pass_fail', unit: 's', tolerance: 10, toleranceMode: 'percent' },
      { label: 'Drive fault signal reaches the BMS', responseType: 'action_reaction_result', toleranceMode: 'none', expectedReaction: 'Fault raised at the BMS' },
      { label: 'Bypass operation where fitted', responseType: 'yes_no_applicable', toleranceMode: 'none' },
    ],
    shotCodes: ['MCC-VSD-PARAM', 'MCC-VSD-RUN'],
  },
];
